import { fetch as expoFetch } from 'expo/fetch';

import { supabase } from '@/lib/supabase/client';
import { generateAPIUrl } from '@/utils';

/**
 * AuthenticatedFetchService wraps API calls with automatic auth header injection,
 * token refresh on 401, and forced signout when authentication is unrecoverable.
 *
 * Usage:
 *   const response = await authenticatedFetch.fetch('/api/some-endpoint', { method: 'POST', body: ... });
 */
export class AuthenticatedFetchService {
  private static instance: AuthenticatedFetchService | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  static getInstance(): AuthenticatedFetchService {
    if (!AuthenticatedFetchService.instance) {
      AuthenticatedFetchService.instance = new AuthenticatedFetchService();
    }
    return AuthenticatedFetchService.instance;
  }

  /**
   * Get the current access token from Supabase session.
   */
  private async getAccessToken(): Promise<string | null> {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }

  /**
   * Attempt to refresh the session token. Deduplicates concurrent refresh attempts.
   */
  private async refreshToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefresh(): Promise<string | null> {
    console.log('[AuthenticatedFetch] Attempting token refresh');
    const { data, error } = await supabase.auth.refreshSession();

    if (error || !data.session?.access_token) {
      console.warn('[AuthenticatedFetch] Token refresh failed:', error?.message);
      return null;
    }

    console.log('[AuthenticatedFetch] Token refreshed successfully');
    return data.session.access_token;
  }

  /**
   * Force sign out the user when authentication is unrecoverable.
   * The SupabaseProvider's onAuthStateChange listener will handle
   * updating UI state and redirecting to sign-in.
   */
  private async forceSignOut(): Promise<void> {
    console.warn('[AuthenticatedFetch] Forcing sign out due to unrecoverable auth failure');
    await supabase.auth.signOut().catch((err) => {
      console.error('[AuthenticatedFetch] Sign out failed:', err);
    });
  }

  /**
   * Make an authenticated API request.
   *
   * @param relativePath - API path (e.g. '/api/home-summary/preferences')
   * @param init - Standard RequestInit options (method, body, headers, etc.)
   * @returns Response object
   * @throws Error if not authenticated and refresh/signout fails
   */
  async fetch(relativePath: string, init: RequestInit = {}): Promise<Response> {
    const url = generateAPIUrl(relativePath);
    const token = await this.getAccessToken();

    if (!token) {
      await this.forceSignOut();
      throw new AuthenticationError('No active session');
    }

    const response = await this.executeRequest(url, init, token);

    if (response.status !== 401) {
      return response;
    }

    // 401 received — attempt token refresh and retry once
    const refreshedToken = await this.refreshToken();

    if (!refreshedToken) {
      await this.forceSignOut();
      throw new AuthenticationError('Session expired and refresh failed');
    }

    return this.executeRequest(url, init, refreshedToken);
  }

  private async executeRequest(
    url: string,
    init: RequestInit,
    token: string,
  ): Promise<Response> {
    const existingHeaders = init.headers as Record<string, string> | undefined;
    const headers: Record<string, string> = {
      ...existingHeaders,
      Authorization: `Bearer ${token}`,
    };

    return expoFetch(url, { ...init, headers } as Parameters<typeof expoFetch>[1]);
  }
}

/**
 * Thrown when authentication is unrecoverable (no session or refresh failed).
 * Callers can catch this specifically to distinguish auth errors from other failures.
 */
export class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

export const authenticatedFetch = AuthenticatedFetchService.getInstance();
